import { genreMatchesFilter } from '.';

export const FILE_TYPES = {
    aiff: {
        mimeTypes: ['audio/aiff', 'audio/x-aiff'],
        extensions: ['aif', 'aiff'],
    },
    csv: {
        mimeTypes: [
            'text/csv',
            'application/csv',
            'application/x-csv',
            'text/comma-separated-values',
            'text/x-comma-separated-values',
            'text/tab-separated-values',
        ],
        extensions: ['csv'],
    },
    mp3: {
        mimeTypes: ['audio/mp3', 'audio/mpeg', 'audio/mpeg3', 'audio/x-mpeg-3'],
        extensions: ['mp3'],
    },
    wav: {
        mimeTypes: ['audio/wav', 'audio/x-wav'],
        extensions: ['wav'],
    },
    jpg: {
        mimeTypes: ['image/jpeg', 'image/pjpeg'],
        extensions: ['jpg', 'jpeg'],
    },
    png: {
        mimeTypes: ['image/png'],
        extensions: ['png'],
    },
} as FileTypes;

export const PAYMENT_TYPES = {
    ADJUSTMENT: 'Adjustment',
    CUE_SHEET: 'Cue Sheet',
    MISC_BULK: 'Misc. Bulk',
    PROXY: 'Proxy',
    REGULAR: 'Regular',
    SAMPLE_SURVEY: 'Sample Survey',
    SETTLEMENT: 'Settlement',
};

export const REGION_LABEL_MAP = new Map<string, string>([
    ['standard' as PRORegionFilter, 'Domestic'],
    ['international' as PRORegionFilter, 'International'],
]);

export const DEFAULT_REGION = {
    filter: 'standard' as PRORegionFilter,
    label: 'Domestic',
};

export const emptySelectionDataValue = {
    ids: [] as string[],
    views: [] as TrqkStatementView[],
    label: {
        long: '',
        short: '',
    },
};

export const broadcastOption = {
    value: 'broadcast' as BroadcastOrDigital,
    label: 'Broadcast / Other',
} as DropdownOption<BroadcastOrDigital>;

export const digitalOption = {
    value: 'digital' as BroadcastOrDigital,
    label: 'Digital',
} as DropdownOption<BroadcastOrDigital>;

export const broadcastVsDigitalOptions = new Map([
    ['broadcast' as BroadcastOrDigital, broadcastOption],
    ['digital' as BroadcastOrDigital, digitalOption],
]);

export const broadcastFilterPredicate = (
    element?: Maybe<MusicUserGenreNameElement>,
): boolean =>
    genreMatchesFilter(element?.music_user_genre_name ?? null, 'broadcast');

export const digitalFilterPredicate = (
    element?: Maybe<MusicUserGenreNameElement>,
): boolean =>
    genreMatchesFilter(element?.music_user_genre_name ?? null, 'digital');

export const ERROR_CODES = {
    AWS: {
        NOT_AUTHORIZED_EXCEPTION: 'NotAuthorizedException',
    },
    TRQK_CLIENT: {
        NO_TOKEN: 'NO_TOKEN',
    },
};

export const API_ERROR_MESSAGES = {
    ACCOUNT_ALREADY_EXISTS: 'An account with the given email already exists.',
    DUPLICATE_OF_STATEMENT: 'Duplicate of statement',
    ALREADY_UPLOADED_THIS_STATEMENT: 'already uploaded this statement',
    INGESTION_TIMEOUT: 'Ingestion timeout',
    NOT_A_VALID_STATEMENT: 'File does not appear to be a valid PRO statement',
    STATEMENT_PREVIOUSLY_ADDED: 'Statement previously added to user',
    TRANSFORMATION_TOO_LARGE: 'Transformation too large',
};

export const CLIENT_ERROR_MESSAGES = {
    TOKEN_MISSING: 'Access token missing',
};

export const statementViewStatusNames: TrqkStatementViewStatusName[] = [
    'PENDING' as TrqkStatementViewStatusName,
    'BUILDING' as TrqkStatementViewStatusName,
    'ANALYZING' as TrqkStatementViewStatusName,
    'RENDERING' as TrqkStatementViewStatusName,
    'COMPLETE' as TrqkStatementViewStatusName,
];

export const SEARCH_FILTER_TYPES = {
    TITLE_FILTER: 'titleFilter',
};

export const SUPPORTED_AUDIO_FILE_TYPES = [
    'mp3',
    'aiff',
    'wav',
] as FileTypeKey[];

export const SUPPORTED_DETECTION_FILE_TYPES = ['csv'] as FileTypeKey[];

export const SUPPORTED_PROS = ['ASCAP', 'BMI', 'SESAC'];

export const LOG_NULL_USER_ID = 'NO__USER';

export const TRIAL_HELP_EMAIL_ADDRESS = 'trial@trqk.io';

export const SUPPORT_EMAIL_ADDRESS = 'support@trqk.io';

export const INFO_EMAIL_ADDRESS = 'info@trqk.io';

export const SUBSCRIPTIONS_SUPPORT_EMAIL_ADDRESS = 'subscriptions+app@trqk.io';

export const SUBSCRIPTIONS_PUBLISHERS_EMAIL_ADDRESS =
    'subscriptions+pub@trqk.io';

export const MAX_AUDIO_UPLOADS_FALLBACK = 100;

export const MAX_AUDIO_UPLOAD_BATCH_SIZE = 30;

export const MAX_AUDIO_FILE_SIZE_MB = 200;

export const MAX_DETECTION_UPLOAD_BATCH_SIZE = 1;

export const MAX_STATEMENTS_PER_USER = 100;

export const MAX_STATEMENT_SIZE_MB = 12;

export const MAX_INVITES = 5;

export const WARNING_STATEMENT_SIZE_MB = 5;

export const MAX_STATEMENT_UPLOAD_BATCH_SIZE = 20;

export const INGESTION_TIMEOUT_MS = 16 * 60 * 1000;

export const LOCAL_STORAGE_KEYS = {
    ENVIRONMENT: 'environment',
};

export const LOG_POLLING_INTERVAL_SHORT = 5 * 1000;

export const LOG_POLLING_INTERVAL_LONG = 30 * 1000;

// Log fetching lookback needs to be longer than the long polling interval. This is to ensure that
// we don't miss any log events in the case where we do not have a startAfter from localStorage and
// the polling is running on the longest possible interval.
export const LOG_FETCHING_LOOKBACK = 1 * 1000 + LOG_POLLING_INTERVAL_LONG;

// If a statement or entity selection context in localStorage is older a than date created by this
// number, it will be ignored and a new context will be generated.
// This number will create a JS date equal to:
//      Wed Mar 31 2021 01:36:52 GMT-0700 (Pacific Daylight Time)
export const ENTITY_AND_STATEMENT_SELECTION_EXPIRATION_DATE = 1617179812313;

export const INGESTION_PROGRESS_CHUNKS: Record<string, number> = {
    upload: 35,
    pending: 50,
    processing: 80,
    complete: 100,
};

export const CONTROL_PANEL_ENVIRONMENTS: Environment[] = ['development'];

// Corresponds to: Fri Jan 01 2500 00:00:00 GMT-0800 (Pacific Standard Time) - we can be sure none
// of the data in Trqk has a timestamp later than this, unless this code somehow manages to live
// much, much longer than expected.
export const LATEST_TIMESTAMP = 16725254400000;

// Corresponds to: Mon Jan 01 2018 00:00:00 GMT-0800 (Pacific Standard Time) - we can be sure none
// of the data in Trqk has a timestamp earlier than this
export const EARLIEST_TIMESTAMP = 1514793600000;

export const QUARTER_DATE_RANGE_MAP: QuarterDateRangeMap = {
    1: {
        start: {
            month: 0, // in JavaScript Date instances, months are zero-indexed
            day: 1,
        },
        end: {
            month: 2,
            day: 31,
        },
    },
    2: {
        start: {
            month: 3,
            day: 1,
        },
        end: {
            month: 5,
            day: 30,
        },
    },
    3: {
        start: {
            month: 6,
            day: 1,
        },
        end: {
            month: 8,
            day: 30,
        },
    },
    4: {
        start: {
            month: 9,
            day: 1,
        },
        end: {
            month: 11,
            day: 31,
        },
    },
};

export const TEST_USERS = {
    PUBLISHER: {
        USERNAME: 'test_publisher@trqk.io',
        ID: 'test-publisher',
    },
};

/**
 * Maps each {@link PreFilterType} to its corresponding AppSync filter key. Useful when converting
 * the state preFilters to query that can be sent to the Trqk Directory API.
 */
export const preFilterTypeToAppSyncFilterTypeMap: Record<
    PreFilterType,
    keyof TableStringFilterInput
> = {
    equals: 'eq',
    lessThanOrEqualTo: 'le',
    greaterThanOrEqualTo: 'ge',
    greaterThan: 'gt',
    confidenceGreaterThanOrEqualTo: 'ge',
};
